import { useEffect, useState } from 'react'
import { Check, Loader2, Search } from 'lucide-react'
import { Modal } from '@/shared/ui/Modal'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import { humanise } from '@/shared/lib/format'
import { cn } from '@/lib/utils'
import type { TransferRequestResponse, UserResponse } from '@/shared/api/types'
import { useSession } from '@/shared/auth/session'
import { EMPTY_USER_FILTERS, listUsers } from '@/features/users/api'
import {
  REJECTION_REASON_LABEL, appealTransfer, approveTransfer, assignTransfer, confirmTransfer,
  failAtIebc, lodgeTransfer, recordTransfer, rejectTransfer, reverseTransfer,
  reviewTransfer, submitTransfer, withdrawTransfer,
} from './api'

/**
 * The action bar, driven entirely by `allowedDecisions` from the server.
 *
 * The backend owns a fifteen-state machine. Deciding in the browser which
 * buttons to show would mean writing a second copy of it, and two copies of a
 * state machine drift — the failure mode being an operator offered a button that
 * 409s, or worse, denied the one action they need with no explanation. So the
 * server says what is legal now and this file only decides what each action
 * *asks for* before it fires.
 *
 * ## A decision this build cannot perform is shown DISABLED, not hidden and not live
 *
 * The first cut rendered any unknown code as a working button. Clicking it opened a
 * notes box, took what the operator typed, and then failed with "This build does not
 * know how to perform ESCALATE" — after the typing, which is the worst possible order.
 *
 * The server is still the authority on what is *legal*; this file is the authority on
 * what this bundle can *call*. Where they disagree the button stays visible — hiding it
 * would leave a reviewer wondering why a colleague can do something they cannot — but it
 * is disabled and says which side is behind.
 */

interface ActionSpec {
  /** Button label. Past-tense-free, and never active voice about moving voters. */
  label: string
  primary?: boolean
  danger?: boolean
  /** What the modal asks for. `none` fires immediately. */
  form: 'none' | 'reason' | 'reject' | 'lodge' | 'appeal' | 'notes' | 'assign'
  title: string
  description: string
  /** Shown above the form when the action is heavier than it looks. */
  warning?: string
}

const ACTIONS: Record<string, ActionSpec> = {
  SUBMIT: {
    label: 'Submit', primary: true, form: 'none',
    title: 'Submit', description: '',
  },
  ASSIGN: {
    label: 'Assign', form: 'assign',
    title: 'Assign this request',
    description: 'Gives it an owner. It does not move the request forward or change its state — someone is simply now responsible for it.',
  },
  REVIEW: {
    label: 'Take under review', form: 'notes',
    title: 'Take under review',
    description: 'Moves this into review and puts your name against it.',
  },
  APPROVE: {
    label: 'Approve', primary: true, form: 'notes',
    title: 'Approve this request',
    description: 'Approval clears it for lodgement with the IEBC. It does not change the register.',
  },
  REJECT: {
    label: 'Reject', danger: true, form: 'reject',
    title: 'Reject this request',
    description: 'A coded reason and a detail, both of which the voter may eventually be told.',
  },
  WITHDRAW: {
    label: 'Withdraw', form: 'reason',
    title: 'Withdraw this request',
    description: 'Nothing is deleted — the request stays with its trail.',
  },
  APPEAL: {
    label: 'Appeal', form: 'appeal',
    title: 'Appeal this decision',
    description: 'One appeal per request, inside the appeal window. It must be reviewed by someone other than whoever rejected it.',
  },
  LODGE: {
    label: 'Lodge with IEBC', primary: true, form: 'lodge',
    title: 'Record that this was lodged with the IEBC',
    description: 'This records that YOU lodged it. The system does not submit anything to the IEBC.',
  },
  CONFIRM: {
    label: 'IEBC confirmed', primary: true, form: 'notes',
    title: 'Record the IEBC’s confirmation',
    description: 'The IEBC has told you the transfer went through.',
  },
  FAIL_AT_IEBC: {
    label: 'IEBC refused', danger: true, form: 'reason',
    title: 'Record the IEBC’s refusal',
    description: 'Their reason, in their words where you have them.',
  },
  RECORD: {
    label: 'Record on our register', primary: true, form: 'notes',
    title: 'Record this on our register',
    description: 'Updates where we hold this voter as registered, and writes a placement-history entry.',
    warning: 'This is the only step that changes a voter record. It is recording something the IEBC already did — not performing a transfer.',
  },
  REVERSE: {
    label: 'Reverse', danger: true, form: 'reason',
    title: 'Reverse this record',
    description: 'For a corrected IEBC confirmation or a data-entry error.',
    warning: 'This writes a compensating history entry. Nothing is deleted and both entries stay visible.',
  },
}

/**
 * Every decision this file has a call for. It mirrors the `switch` in `run` below and
 * must be edited with it — the pair is what stops a button existing for a code that
 * falls through to "this build does not know how to perform it".
 *
 * `ESCALATE` is deliberately absent: the API has no escalate endpoint (escalation is
 * raised by `TransferSlaService` when an SLA passes), so there is nothing to call.
 */
const PERFORMABLE = new Set([
  'SUBMIT', 'REVIEW', 'ASSIGN', 'APPROVE', 'REJECT', 'WITHDRAW', 'APPEAL',
  'LODGE', 'CONFIRM', 'FAIL_AT_IEBC', 'RECORD', 'REVERSE',
])

export function TransferActions({
  request, allowed, onDone,
}: {
  request: TransferRequestResponse
  allowed: string[]
  onDone: () => void
}) {
  const [open, setOpen] = useState<string | null>(null)

  // CONSENT_CAPTURED and WAIVE_GATE are legal decisions but they are captured by
  // their own dedicated UI, not by a generic button.
  const shown = allowed.filter((d) => d !== 'CONSENT_CAPTURED' && d !== 'WAIVE_GATE' && d !== 'COMMENT')

  if (shown.length === 0) {
    return (
      <p className="text-erp-xs text-muted-foreground">
        No action is available from <strong>{humanise(request.status)}</strong> with your
        authorities.
      </p>
    )
  }

  return (
    <>
      <div className="flex flex-wrap gap-1.5">
        {shown.map((decision) => {
          const spec = ACTIONS[decision]
          const supported = Boolean(spec) && PERFORMABLE.has(decision)
          return (
            <button
              key={decision}
              type="button"
              disabled={!supported}
              title={supported
                ? undefined
                : `The server allows ${humanise(decision)} from here, but this build has no call for it. Update the app, or ask for it to be removed from the workflow.`}
              className={cn(
                spec?.primary ? 'erp-btn erp-btn-primary'
                  : spec?.danger ? 'erp-btn text-red-700 dark:text-red-400'
                    : 'erp-btn',
                !supported && 'opacity-50 cursor-not-allowed',
              )}
              onClick={() => supported && setOpen(decision)}
            >
              {spec?.label ?? humanise(decision)}
            </button>
          )
        })}
      </div>
      {shown.some((d) => !PERFORMABLE.has(d)) && (
        <p className="text-erp-xs text-muted-foreground mt-1.5">
          Greyed-out actions are ones the server offers and this build cannot perform yet.
        </p>
      )}
      {open && (
        <ActionModal
          decision={open}
          request={request}
          onClose={() => setOpen(null)}
          onDone={() => { setOpen(null); onDone() }}
        />
      )}
    </>
  )
}

/**
 * Who owns this request.
 *
 * Assignment is the one action here that changes nothing about the request's *state* —
 * it puts a name against it so a queue of forty has forty owners rather than forty
 * orphans. That is why its modal asks for a person and not for notes: a notes box was
 * the old UI, and it collected a sentence nobody could act on while the field the API
 * actually requires — `assigneeUserId` — was never asked for at all.
 *
 * "Assign to me" is first because it is the common case: someone picks up a request they
 * are already looking at.
 */
function AssigneePicker({ value, onPick }: { value: string; onPick: (id: string) => void }) {
  const me = useSession((s) => s.user)
  const [q, setQ] = useState('')
  const [results, setResults] = useState<UserResponse[]>([])
  const [loading, setLoading] = useState(false)
  const [picked, setPicked] = useState<UserResponse | null>(null)

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return }
    let cancelled = false
    setLoading(true)
    const handle = setTimeout(() => {
      listUsers({ ...EMPTY_USER_FILTERS, q: q.trim(), status: ['ACTIVE'] }, 0, 8)
        .then((page) => { if (!cancelled) setResults(page.content) })
        .catch(() => { if (!cancelled) setResults([]) })
        .finally(() => { if (!cancelled) setLoading(false) })
    }, 300)
    return () => { cancelled = true; clearTimeout(handle) }
  }, [q])

  if (value) {
    return (
      <div>
        <span className="text-erp-xs text-muted-foreground">Owner</span>
        <div className="mt-1 flex items-center gap-2 px-3 h-9 border border-chocolate/50 rounded-md bg-chocolate-soft/30">
          <Check className="h-3.5 w-3.5 text-chocolate shrink-0" />
          <span className="text-erp-sm truncate flex-1">
            {picked?.name ?? (value === me?.id ? `${me?.name} (you)` : value)}
          </span>
          <button type="button" className="text-erp-xs text-muted-foreground hover:text-foreground shrink-0"
                  onClick={() => { onPick(''); setPicked(null) }}>
            Change
          </button>
        </div>
      </div>
    )
  }

  return (
    <div data-picker="Owner">
      <span className="text-erp-xs text-muted-foreground">
        Owner <span className="text-red-600">*</span>
      </span>
      {me && (
        <button
          type="button"
          className="erp-btn w-full mt-1 justify-center"
          onClick={() => { onPick(me.id); setPicked(me) }}
        >
          Assign to me
        </button>
      )}
      <div className="mt-1.5 flex items-center gap-2 px-3 h-9 border border-input rounded-md bg-background">
        <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Or search a colleague by name or email"
          aria-label="Owner"
          className="bg-transparent outline-none text-erp-sm w-full min-w-0"
        />
        {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground shrink-0" />}
      </div>
      {results.length > 0 && (
        <div className="mt-1 erp-card max-h-40 overflow-y-auto py-1">
          {results.map((u) => (
            <button
              key={u.id}
              type="button"
              onClick={() => { onPick(u.id); setPicked(u) }}
              className="w-full px-3 py-1.5 text-left text-erp-sm hover:bg-muted"
            >
              {u.name}
              <span className="text-erp-xs text-muted-foreground ml-2">{u.email}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function ActionModal({
  decision, request, onClose, onDone,
}: {
  decision: string
  request: TransferRequestResponse
  onClose: () => void
  onDone: () => void
}) {
  const spec = ACTIONS[decision]
  const [text, setText] = useState('')
  const [reasonCode, setReasonCode] = useState('VOTER_DECLINED')
  const [iebcReference, setIebcReference] = useState('')
  const [assigneeId, setAssigneeId] = useState(request.assignedToUserId ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const form = spec?.form ?? 'notes'
  const needsText = form === 'reason' || form === 'reject' || form === 'appeal'
  const minLength = form === 'appeal' ? 20 : 5
  const ready =
    form === 'none' || form === 'notes' ? true
      : form === 'lodge' ? iebcReference.trim() !== ''
        : form === 'assign' ? assigneeId !== ''
          : text.trim().length >= minLength

  const run = async () => {
    setSaving(true); setError(null)
    try {
      switch (decision) {
        case 'SUBMIT': await submitTransfer(request.id); break
        case 'ASSIGN': await assignTransfer(request.id, assigneeId, text.trim() || undefined); break
        case 'REVIEW': await reviewTransfer(request.id, text.trim() || undefined); break
        // Approve is version-checked: it is the gate to lodgement, and two people
        // approving the same request from stale screens must not both succeed.
        case 'APPROVE': await approveTransfer(request.id, request.version, text.trim() || undefined); break
        case 'REJECT': await rejectTransfer(request.id, reasonCode, text.trim()); break
        case 'WITHDRAW': await withdrawTransfer(request.id, text.trim()); break
        case 'APPEAL': await appealTransfer(request.id, text.trim()); break
        case 'LODGE': await lodgeTransfer(request.id, iebcReference.trim(), text.trim() || undefined); break
        case 'CONFIRM': await confirmTransfer(request.id, text.trim() || undefined); break
        case 'FAIL_AT_IEBC': await failAtIebc(request.id, text.trim()); break
        case 'RECORD': await recordTransfer(request.id, text.trim() || undefined); break
        case 'REVERSE': await reverseTransfer(request.id, text.trim()); break
        default:
          throw new Error(`This build does not know how to perform "${decision}".`)
      }
      onDone()
    } catch (e) { setError(explain(e)); setSaving(false) }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={spec?.title ?? humanise(decision)}
      description={spec?.description ?? ''}
      footer={
        <>
          <button type="button" className="erp-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            type="button"
            className="erp-btn erp-btn-primary"
            disabled={!ready || saving}
            onClick={() => void run()}
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {spec?.label ?? 'Confirm'}
          </button>
        </>
      }
    >
      <ErrorStrip message={error} />

      {spec?.warning && (
        <div className="mb-3"><Note tone="bad">{spec.warning}</Note></div>
      )}

      {form === 'assign' && (
        <div className="mb-3">
          <AssigneePicker value={assigneeId} onPick={setAssigneeId} />
        </div>
      )}

      {form === 'lodge' && (
        <label className="block mb-3">
          <span className="text-erp-xs text-muted-foreground">
            IEBC reference <span className="text-red-600">*</span>
          </span>
          <input
            className="erp-input w-full mt-1 font-mono"
            value={iebcReference}
            onChange={(e) => setIebcReference(e.target.value)}
            placeholder="The reference the IEBC gave you"
          />
          <p className="text-erp-xs text-muted-foreground mt-1">
            Required. Without a reference there is nothing to trace this back to, so the API
            refuses the lodgement and so does the database.
          </p>
        </label>
      )}

      {form === 'reject' && (
        <label className="block mb-3">
          <span className="text-erp-xs text-muted-foreground">Reason</span>
          <select
            className="erp-input w-full mt-1"
            value={reasonCode}
            onChange={(e) => setReasonCode(e.target.value)}
          >
            {Object.entries(REJECTION_REASON_LABEL).map(([code, label]) => (
              <option key={code} value={code}>{label}</option>
            ))}
          </select>
        </label>
      )}

      {/* A `none` action sends no body at all, so there is no field to type into
          — offering one would drop whatever was written in it. */}
      {form !== 'none' && (
      <label className="block">
        <span className="text-erp-xs text-muted-foreground">
          {form === 'appeal' ? 'Why should this be reconsidered?'
            : needsText ? 'Detail' : 'Notes'}
          {needsText && <span className="text-red-600"> *</span>}
          {!needsText && <span className="opacity-70"> (optional)</span>}
        </span>
        <textarea
          className="erp-input w-full mt-1 min-h-[72px]"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        {needsText && text.trim().length > 0 && text.trim().length < minLength && (
          <p className="text-erp-xs text-muted-foreground mt-1">
            At least {minLength} characters — this goes in the permanent trail.
          </p>
        )}
      </label>
      )}
    </Modal>
  )
}
